import React, { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { formatPhoneNumber } from "../../utils/phoneNumberUtils";
import { getParticipantCoords } from "../../utils/geocode";

// Fix default marker icons (webpack drops the relative paths otherwise)
const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const driverIcon = L.divIcon({
  className: "",
  html: '<div style="background:#ff9100;color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4);font-size:14px;">🚗</div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const ParticipantsMapModal = ({ isOpen, onClose, participants = [] }) => {
  const points = useMemo(
    () =>
      (participants || [])
        .map((participant) => ({
          ...participant,
          coords: getParticipantCoords(participant),
        }))
        .filter((participant) => participant.coords),
    [participants],
  );

  const center = useMemo(() => {
    if (points.length === 0) return [39.8283, -98.5795]; // continental US fallback
    const lat =
      points.reduce((sum, p) => sum + p.coords.latitude, 0) / points.length;
    const lng =
      points.reduce((sum, p) => sum + p.coords.longitude, 0) / points.length;
    return [lat, lng];
  }, [points]);

  if (!isOpen) return null;

  const missing = (participants || []).length - points.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl flex flex-col"
        style={{ maxHeight: "90vh" }}
      >
        <div className="flex justify-between items-center p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold">Participants on Map</h3>
            <p className="text-xs text-gray-500">
              {points.length} plotted
              {missing > 0
                ? ` · ${missing} missing GPS (use "Fill in GPS" on the Members tab)`
                : ""}
            </p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="flex-1 overflow-hidden">
          {points.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No participants have GPS coordinates yet. Use the &quot;Fill in
              GPS&quot; option on the Members tab to plot them here.
            </div>
          ) : (
            <MapContainer
              center={center}
              zoom={points.length === 1 ? 13 : 11}
              style={{ width: "100%", height: "70vh" }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {points.map((p) => (
                <Marker
                  key={p.id || p.participantId}
                  position={[p.coords.latitude, p.coords.longitude]}
                  icon={p.isDriver ? driverIcon : DefaultIcon}
                >
                  {p.name && (
                    <Tooltip
                      permanent
                      direction="top"
                      offset={[0, p.isDriver ? -16 : -34]}
                      className="participant-map-label"
                    >
                      {p.name}
                    </Tooltip>
                  )}
                  <Popup>
                    <div className="text-sm">
                      <div className="font-semibold">{p.name}</div>
                      {p.isDriver && (
                        <div className="text-orange-600 text-xs">Driver</div>
                      )}
                      {p.rideLocation && (
                        <div className="text-gray-700">{p.rideLocation}</div>
                      )}
                      {p.pickupGeocodedAddress && (
                        <div className="text-gray-500 text-xs mt-1">
                          {p.pickupGeocodedAddress}
                        </div>
                      )}
                      {p.phone && (
                        <div className="text-gray-500 text-xs mt-1">
                          {formatPhoneNumber(p.phone)}
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParticipantsMapModal;
